import { addDays, format } from '@if/core/date'
import { EDocumentStatus, EDocumentType, EFinancingProgram, ERequiredFrom } from '@if/core/types'
import { formatDate, formatDateTime } from '@if/core/utils'
import {
  CheckCircle as CheckCircleIcon,
  DeleteForever as DeleteForeverIcon,
  DocumentScanner as DocumentScannerIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  FindInPage as FindInPageIcon,
  HighlightOff,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  NotificationsActive as NotificationActiveIcon,
  PendingActions as PendingActionsIcon,
  RemoveCircle as RemoveCircleIcon,
} from '@mui/icons-material'
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Select,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useDownloadDocument } from '@src/data/api/document/document-api'
import { useRequiredDocumentStatusFromMatchingDocuments } from '@src/data/selectors/document-selectors'
import { IFinanceBaseWorksheet } from '@src/data/types/IFinanceBaseWorksheetSchema'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IconButtonWithTooltip } from '../../../components'
import { useCurrentLang, useDocumentTypes } from '../../../data/selectors/app-config-selectors'
import { Constants, UserRights } from '../../../data/types'
import { Document, DocumentRequired } from '../../../data/types/DocumentSchema'
import { DocumentUploadArea } from './documentUploadArea'

export enum DocumentDialog {
  DocumentsRequest = 'documentsRequestDialog',
  EditDocument = 'editDocumentRequestDialog',
  SendDocument = 'sendDocumentDialog',
  DeleteDocumentRequest = 'deleteDocumentRequest',
  None = '',
}

interface RowPops {
  documentRequired: DocumentRequired
  documents: Document[]
  setOpenDialog: (data: DocumentDialog) => void
  setEditRequiredDocument: (data: DocumentRequired) => void
  handleDelete?: (data: DocumentRequired) => void
  sendDocs: (documentList: Document[], files: File[]) => void
  isUploading: boolean
  editDisabled: boolean
  financingProgramId: EFinancingProgram
  userCanEditDocument: boolean
  userRights: UserRights | undefined
  handleDocumentChecked: (documentId: string) => (e: React.ChangeEvent<HTMLInputElement>) => void
  handleUpdateDocumentRequest: (documentRequired: DocumentRequired) => void
  selectedDocumentIds: string[]
  worksheet: IFinanceBaseWorksheet | null
  showRequiredFromColumn?: boolean
}

const DocumentTableRow = ({
  documentRequired,
  documents,
  setOpenDialog,
  setEditRequiredDocument,
  handleDelete,
  sendDocs,
  isUploading,
  editDisabled,
  financingProgramId,
  userCanEditDocument,
  userRights,
  handleDocumentChecked,
  selectedDocumentIds,
  handleUpdateDocumentRequest,
  worksheet,
  showRequiredFromColumn = true,
}: RowPops) => {
  const { t } = useTranslation()

  const isDocumentSubmitted = () => {
    return documents.length > 0
  }
  const isSingleDocument = () => {
    return documents.length === 1
  }
  const [selectedValue, setSelectedValue] = useState<ERequiredFrom>(
    documentRequired.requiredFrom || ERequiredFrom.Customer,
  )

  const [open, setOpen] = useState<boolean>(!isSingleDocument() && isDocumentSubmitted())
  const [currentDownloads, setCurrentDownloads] = useState<string[]>([])

  const theme = useTheme()
  const documentTypes = useDocumentTypes()
  const lang = useCurrentLang()

  const [downloadDocumentMutation] = useDownloadDocument()

  const { documents: allDocs, requiredDocuments: allReqDocs } = useCreditApplicationData()
  const documentStatuses = useRequiredDocumentStatusFromMatchingDocuments(allDocs, allReqDocs)
  const firstDocumentStatus = useMemo(() => {
    if (documents.length === 1) return documents[0].status
    return documentStatuses[String(documentRequired.id)]
  }, [documents, documentRequired.id, documentStatuses])

  const firstDocument = {
    ...documents[documents.length - 1],
    status: firstDocumentStatus,
    receivedOn: documents[0]?.createdOn,
  }
  const history = [...documents]
  history.sort(
    (a, b) => new Date(`${b.receivedOn?.toString()}`).getTime() - new Date(`${a.receivedOn?.toString()}`).getTime(),
  )

  let formattedDate: string | null
  if (firstDocument?.updatedOn) {
    formattedDate = formatDate(firstDocument.updatedOn)
  } else {
    formattedDate = null
  }

  const updatedBy =
    firstDocument.updatedByUserFullname === 'System' ? t('common.system') : firstDocument.updatedByUserFullname

  function canSendDoc() {
    return (
      firstDocument?.status !== EDocumentStatus.AwaitingScan &&
      (!firstDocument?.status ||
        firstDocument?.status === EDocumentStatus.Deleted ||
        firstDocument?.status === EDocumentStatus.Refused)
    )
  }

  function canSendOrDeleteHighEstimateInvoice(firstPaymentOn: string) {
    const maxDateToSendInvoice = format(addDays(new Date(firstPaymentOn), -5), 'yyyy-MM-dd')
    const today = format(new Date(), 'yyyy-MM-dd')
    return today <= maxDateToSendInvoice && canSendDoc()
  }
  const isHighEstimateClinicInvoice =
    worksheet?.isHighEstimate && documentRequired.typeId === EDocumentType.ClinicInvoice

  const canShowUpload =
    !editDisabled &&
    (isHighEstimateClinicInvoice ? canSendOrDeleteHighEstimateInvoice(worksheet.firstPaymentOn) : canSendDoc())

  const canShowDelete =
    documentRequired.typeId === EDocumentType.ClinicInvoice &&
    documentRequired.createdByUserId === Constants.SystemUserId &&
    worksheet?.isHighEstimate
      ? canSendOrDeleteHighEstimateInvoice(worksheet.firstPaymentOn)
      : handleDelete !== undefined

  function canDownloadDocuments() {
    return (
      firstDocument?.status &&
      firstDocument?.status !== EDocumentStatus.AwaitingScan &&
      firstDocument?.status !== EDocumentStatus.Deleted
    )
  }

  const downloadDocument = (document: Document) => {
    setCurrentDownloads((prev) => [...prev, document.id!])

    return downloadDocumentMutation({
      documentId: document.id ?? '',
      creditApplicationId: document.creditApplicationId,
      financingProgramId,
    })
      .catch(reportErrorToConsole)
      .finally(() => {
        setCurrentDownloads((prev) => prev.filter((i) => i !== document.id))
      })
  }

  const awaitingScan = EDocumentStatus.AwaitingScan
  const deleted = EDocumentStatus.Deleted
  const showLink =
    firstDocument &&
    firstDocument.status !== awaitingScan &&
    firstDocument.status !== deleted &&
    isSingleDocument() &&
    isDocumentSubmitted()

  const getDocumentStatusIcon = (document: Document) => {
    return (
      <>
        {!document?.status && (
          <Tooltip title={t('document.awaitingDocument')} arrow placement="top">
            <PendingActionsIcon sx={{ color: theme.palette.warning.main }} />
          </Tooltip>
        )}
        {document?.status === EDocumentStatus.AwaitingScan && (
          <Tooltip title={t('document.awaitingScan')} arrow placement="top">
            <DocumentScannerIcon />
          </Tooltip>
        )}
        {document?.status === EDocumentStatus.AwaitingApproval && (
          <Tooltip title={t('document.awaitingApproval')} arrow placement="top">
            <FindInPageIcon color="primary" />
          </Tooltip>
        )}
        {document?.status === EDocumentStatus.Approved && (
          <Tooltip
            title={`${t('document.approvedOn')} ${formattedDate} ${t('common.by').toLowerCase()} ${updatedBy}`}
            arrow
            placement="top"
          >
            <CheckCircleIcon color="success" />
          </Tooltip>
        )}
        {document?.status === EDocumentStatus.Deleted && (
          <Tooltip
            title={`${t('document.deletedOn')} ${formattedDate} ${t('common.by').toLowerCase()} ${updatedBy}`}
            arrow
            placement="top"
          >
            <HighlightOff color="error" />
          </Tooltip>
        )}
        {document?.status === EDocumentStatus.Refused && (
          <Tooltip
            title={`${t('document.refusedOn')} ${formattedDate} ${t('common.by').toLowerCase()} ${updatedBy}`}
            arrow
            placement="top"
          >
            <RemoveCircleIcon color="error" />
          </Tooltip>
        )}
        {document?.status === EDocumentStatus.Incomplete && (
          <Tooltip
            title={`${t('document.incompleteOn')} ${formattedDate} ${t('common.by').toLowerCase()} ${updatedBy}`}
            arrow
            placement="top"
          >
            <NotificationActiveIcon sx={{ color: theme.palette.warning.main }} />
          </Tooltip>
        )}
      </>
    )
  }

  const getDocumentSubText = (document: Document) => {
    return (
      <>
        {document.note && (
          <p style={{ marginLeft: '4.4rem', whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
            <strong>{`${t('document.uploadNote')}:`}</strong> {document.note}
          </p>
        )}
        {document?.refusalReason &&
          (document?.status === EDocumentStatus.Refused ||
            document?.status === EDocumentStatus.Deleted ||
            document?.status === EDocumentStatus.Incomplete) && (
            <p style={{ marginLeft: '4.4rem', whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
              <strong>{t('document.decision')}:</strong> {document.refusalReason}
            </p>
          )}
      </>
    )
  }

  return (
    <>
      <TableRow key={documentRequired.id} sx={{ verticalAlign: 'center' }}>
        <TableCell align="left" sx={{ minWidth: '27em', maxWidth: '27em', borderBottom: 'none' }}>
          {showLink && (
            <Stack direction="row">
              {getDocumentStatusIcon(firstDocument)}
              <FormControlLabel
                sx={{ marginTop: '-0.5rem', marginLeft: '0.3rem' }}
                control={
                  <Checkbox
                    color={firstDocument?.id === selectedDocumentIds[0] ? 'warning' : 'primary'}
                    checked={selectedDocumentIds.includes(firstDocument.id!)}
                    onChange={handleDocumentChecked(firstDocument.id!)}
                  />
                }
                label={
                  <span>
                    <Link
                      href={`/Applications/${financingProgramId}/${
                        documentRequired.creditApplicationId
                      }/split-documents?docIds=${encodeURI(`${firstDocument?.id}`)}`}
                      underline="none"
                    >
                      {documentTypes[documentRequired.typeId][lang]}
                      {documentRequired.labelSuffix && ` - ${documentRequired.labelSuffix}`}
                      {documentRequired.applicantType ? ` - ${t(`common.${documentRequired.applicantType}`)}` : ''}
                    </Link>
                  </span>
                }
              />
            </Stack>
          )}

          {!showLink && (
            <Stack direction="row">
              <span style={{ marginRight: '1rem', marginTop: '-0.2rem' }}>{getDocumentStatusIcon(firstDocument)}</span>
              <Typography>
                {documentTypes[documentRequired.typeId][lang]}
                {documentRequired.labelSuffix && ` - ${documentRequired.labelSuffix}`}
                {documentRequired.applicantType ? ` - ${t(`common.${documentRequired.applicantType}`)}` : ''}
              </Typography>
            </Stack>
          )}
          {documentRequired.note && (
            <p style={{ marginLeft: '4.4rem', whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
              <strong>{`${t('document.agentComment')}:`}</strong> {documentRequired.note}
            </p>
          )}
          {getDocumentSubText(firstDocument)}
        </TableCell>
        <TableCell align="left" sx={{ borderBottom: 'none' }}>
          {documentRequired.requiredBeforeStep
            ? t(`ECreditApplicationStep.${documentRequired.requiredBeforeStep}`, { ns: 'enum' })
            : ''}
        </TableCell>
        {showRequiredFromColumn && (
          <TableCell align="left" sx={{ borderBottom: 'none' }}>
            <Select
              disabled={editDisabled || !userCanEditDocument}
              value={selectedValue}
              onChange={(event) => {
                const newValue = event.target.value as ERequiredFrom
                setSelectedValue(newValue)
                const updatedDocumentRequired = { ...documentRequired, requiredFrom: newValue, financingProgramId }
                handleUpdateDocumentRequest(updatedDocumentRequired)
              }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value={ERequiredFrom.Customer}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <EmailIcon fontSize="small" />
                  <span>{t('ERequiredFrom.customer', { ns: 'enum' })}</span>
                </Stack>
              </MenuItem>
              <MenuItem value={ERequiredFrom.ServiceProvider}>
                {t('ERequiredFrom.serviceProvider', { ns: 'enum' })}
              </MenuItem>
              <MenuItem value={ERequiredFrom.CreditAgent}>{t('ERequiredFrom.creditAgent', { ns: 'enum' })}</MenuItem>
            </Select>
          </TableCell>
        )}

        <TableCell align="left" sx={{ borderBottom: 'none' }}>
          {formatDate(firstDocument?.receivedOn)}
        </TableCell>
        <TableCell align="left" sx={{ borderBottom: 'none' }}>
          {formatDate(documentRequired.createdOn)}
        </TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>
          <Stack direction="row" marginTop="-0.5rem">
            {documentRequired.id && documentRequired.createdByUserId !== Constants.SystemUserId && (
              <IconButtonWithTooltip
                disabled={editDisabled || !userCanEditDocument}
                tooltip={t('document.edit')}
                icon={<EditIcon />}
                onClick={() => {
                  setOpenDialog(DocumentDialog.EditDocument)
                  setEditRequiredDocument(documentRequired)
                }}
              />
            )}
            {handleDelete !== undefined && canShowDelete && (
              <IconButtonWithTooltip
                disabled={editDisabled || !userCanEditDocument}
                tooltip={t('document.deleteDocumentRequest')}
                icon={<DeleteForeverIcon />}
                onClick={() => handleDelete(documentRequired)}
              />
            )}

            {canShowUpload && (
              <DocumentUploadArea
                financingProgramId={financingProgramId}
                documentRequired={documentRequired}
                sendDocs={sendDocs}
                isUploading={isUploading}
              />
            )}
            {canDownloadDocuments() && userRights?.canDownloadDocuments && isSingleDocument() && (
              <IconButtonWithTooltip
                tooltip={t('common.download')}
                icon={currentDownloads.includes(firstDocument.id!) ? <CircularProgress size={25} /> : <DownloadIcon />}
                onClick={() => downloadDocument(firstDocument)}
              />
            )}
            {documents.length > 1 && (
              <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </Stack>
        </TableCell>
      </TableRow>
      {open && documents.length > 1 && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2 }}>
                {history.map((historyDoc: Document) => (
                  <div key={historyDoc.id}>
                    <Stack direction="row" alignItems="center">
                      {getDocumentStatusIcon(historyDoc)}
                      <FormControlLabel
                        sx={{ marginLeft: '0.5rem' }}
                        control={
                          <Checkbox
                            disabled={historyDoc.status === deleted}
                            color={historyDoc.id === selectedDocumentIds[0] ? 'warning' : 'primary'}
                            checked={selectedDocumentIds.includes(historyDoc.id!)}
                            onChange={handleDocumentChecked(historyDoc.id!)}
                          />
                        }
                        label={
                          <span>
                            {historyDoc.status === deleted ? (
                              `${t('document.uploadedOn')} ${formatDateTime(historyDoc.updatedOn)}`
                            ) : (
                              <Link
                                href={`/Applications/${financingProgramId}/${
                                  documentRequired.creditApplicationId
                                }/split-documents?docIds=${encodeURI(`${historyDoc.id}`)}`}
                                underline="none"
                              >
                                {`${t('document.uploadedOn')} ${formatDateTime(historyDoc.updatedOn)}`}
                              </Link>
                            )}
                          </span>
                        }
                      />
                      {canDownloadDocuments() && userRights?.canDownloadDocuments && !isSingleDocument() && (
                        <IconButtonWithTooltip
                          tooltip={t('common.download')}
                          icon={
                            currentDownloads.includes(firstDocument.id!) ? (
                              <CircularProgress size={25} />
                            ) : (
                              <DownloadIcon />
                            )
                          }
                          onClick={() => downloadDocument(historyDoc)}
                        />
                      )}
                    </Stack>
                    {getDocumentSubText(historyDoc)}
                  </div>
                ))}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

export default DocumentTableRow
